import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  Divider,
  Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '60%',
  maxHeight: '80vh',
  overflowY: 'auto',
  backgroundColor: '#fff',
  padding: 4,
  borderRadius: 4,
  boxShadow: 24,
  outline: 'none',
};

const ComentariosEntregaModal = ({ open, onClose, onSave, initialComentarios = [] }) => {
  const [comentarioInput, setComentarioInput] = useState('');
  const [comentarios, setComentarios] = useState([]);
  const [comentariosEliminados, setComentariosEliminados] = useState([]); // ✅ Rastrear eliminados

  // ✅ Función para formatear fecha
  const formatoFecha = (fecha) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    return date.toLocaleString('es-HN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    // Asegurar que los comentarios tengan el formato correcto
    const comentariosFormateados = initialComentarios.map(c => ({
      id: c.id || null, // Preservar ID si existe (comentario existente)
      comentarios: c.comentarios || c.comentario || '', // Manejar ambos formatos
      fechaIngreso: c.fechaIngreso,
      ingresadoPor: c.ingresadoPor,
      isNew: !c.id // Marcar si es un comentario nuevo
    }));
    setComentarios(comentariosFormateados);
    setComentariosEliminados([]); // Resetear eliminados al abrir modal
  }, [initialComentarios, open]);

  const handleAgregarComentario = () => {
    if (comentarioInput.trim() === '') return;
    const nuevoComentario = { 
      id: null, // Nuevo comentario sin ID
      comentarios: comentarioInput.trim(),
      isNew: true 
    };
    setComentarios(prev => [...prev, nuevoComentario]);
    setComentarioInput('');
  };

  const handleEliminarComentario = (index) => {
    const comentarioAEliminar = comentarios[index];
    
    // ✅ Si el comentario tiene ID (existe en BD), agregarlo a la lista de eliminados
    if (comentarioAEliminar.id) {
      setComentariosEliminados(prev => [...prev, comentarioAEliminar.id]);
    }
    
    // Remover del array local
    setComentarios(prev => prev.filter((_, i) => i !== index));
  };

  const handleGuardar = () => {
    // Solo enviar comentarios que no están vacíos
    const comentariosValidos = comentarios
      .filter(c => c.comentarios && c.comentarios.trim() !== '')
      .map(c => ({
        id: c.id,
        comentarios: c.comentarios,
        isNew: c.isNew
      }));
    
    // ✅ Pasar comentarios válidos Y los IDs de comentarios eliminados
    onSave(comentariosValidos, comentariosEliminados);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
          Comentarios de Entrega
        </Typography>

        {/* ✅ Resumen de comentarios */}
        {comentarios.length > 0 && (
          <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
            Total: {comentarios.length} comentario(s) 
            {comentarios.some(c => !c.isNew) && ` (${comentarios.filter(c => !c.isNew).length} existente(s))`}
            {comentarios.some(c => c.isNew) && ` (${comentarios.filter(c => c.isNew).length} nuevo(s))`}
            {comentariosEliminados.length > 0 && ` - ${comentariosEliminados.length} marcado(s) para eliminar`}
          </Typography>
        )}

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Comentario"
              fullWidth
              multiline
              maxRows={4}
              value={comentarioInput}
              onChange={(e) => setComentarioInput(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAgregarComentario}
              fullWidth
            >
              Agregar Comentario
            </Button>
          </Grid>

          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Comentario</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Usuario</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comentarios.map((c, index) => (
                    <TableRow key={c.id || index}>
                      <TableCell>
                        {c.comentarios || ''}
                        {c.isNew && (
                          <span style={{ color: '#1976d2', fontSize: '0.8em', marginLeft: '8px' }}>
                            (Nuevo)
                          </span>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85em', color: '#666' }}>
                        {c.fechaIngreso ? formatoFecha(c.fechaIngreso) : '-'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85em', color: '#666' }}>
                        {c.ingresadoPor || '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton onClick={() => handleEliminarComentario(index)}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {comentarios.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" style={{ color: '#666', fontStyle: 'italic' }}>
                        No hay comentarios agregados
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Botones de acción */}
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="flex-end" spacing={2} mt={2}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CloseIcon />}
                onClick={onClose}
              >
                Cerrar
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                onClick={handleGuardar}
              >
                Guardar
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default ComentariosEntregaModal;
